/* @flow */
import * as React from "react";
import PropTypes from "prop-types";
import { withColorsProvider, type ColorSet } from "../ColorsProvider";
import prefixStyles, { type StylesObject } from "../utils/prefixStyles";
import Theme from "../Theme";
import Tappable, { simpleActiveBackgroundColor } from "../Tappable";
import NoStyleButton from "../NoStyleButton";

const ITEM_PADDING = Theme.Padding.CONTENT;

type PropsWithoutColors = {
  children?: ?React.Node,
  id: string | number,
  childWrapperStyle?: ?StylesObject,
  labelWrapperStyle?: ?StylesObject,
  needsTrustedTap?: ?boolean,
  onTap?: ?() => void,
  label?: ?React.Node,
  selected?: ?boolean,
  selectedStyle?: ?StylesObject,
  activeStyle?: ?StylesObject,
  style?: ?StylesObject,
  updateKey?: ?string,
  holdActiveUntil?: ?() => Promise<mixed>,
};

type PropsWithColors = PropsWithoutColors & {
  colors: ColorSet,
};

class Item extends React.Component<PropsWithColors> {
  static propTypes = {
    children: PropTypes.node,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    childWrapperStyle: PropTypes.object,
    labelWrapperStyle: PropTypes.object,
    needsTrustedTap: PropTypes.bool,
    onTap: PropTypes.func,
    label: PropTypes.node,
    selected: PropTypes.bool,
    selectedStyle: PropTypes.object,
    activeStyle: PropTypes.object,
    style: PropTypes.object,
    updateKey: PropTypes.string,
    holdActiveUntil: PropTypes.func,
  };

  shouldComponentUpdate(nextProps) {
    // ItemLists can become very large, so we want to control which Items actually render.
    // It is slower do a deep compare of props like "children" than to render, and shallow
    // comparison fails with most Object props. Allow consumers to define criteria on the fly
    // with an "updateKey".
    // Update if no updateKey is provided, or if it is different than the current key.
    return !(
      nextProps.updateKey && nextProps.updateKey === this.props.updateKey
    );
  }

  render() {
    const {
      children,
      id,
      labelWrapperStyle,
      childWrapperStyle,
      needsTrustedTap,
      onTap,
      label,
      selected,
      selectedStyle,
      activeStyle,
      style,
      // eslint-disable-next-line no-unused-vars
      updateKey,
      holdActiveUntil,
      colors,
      ...otherProps
    } = this.props;

    return (
      <Tappable
        onTap={onTap}
        needsTrustedTap={needsTrustedTap}
        holdActiveUntil={holdActiveUntil}
      >
        {({ active, tapTargetRefHandler }) => {
          const styles = prefixStyles({
            item: {
              display: "flex",
              alignItems: "center",

              whiteSpace: "normal",
              color: colors.TEXT,

              outline: "none",
              transition: "color 0.1s",

              cursor: onTap ? "pointer" : "default",
              backgroundColor: simpleActiveBackgroundColor(
                onTap != null && active
              ),
              ...style,
              ...(active ? activeStyle : null),
              ...(selected
                ? {
                    color: colors.PRIMARY,
                    ...selectedStyle,
                  }
                : null),
            },
            label: ({
              paddingTop: ITEM_PADDING,
              paddingRight: ITEM_PADDING,
              paddingBottom: ITEM_PADDING,
              paddingLeft: 0,
              minWidth: 0, // fixes a bizarre bug with text-overflow
              ...Theme.Styles.flexStretch,
              ...labelWrapperStyle,
            }: StylesObject),
            child: {
              whiteSpace: "normal",
              height: "100%",
              flex: "0 0 auto",
              minWidth: 0, // fixes a bizarre bug with text-overflow
              ...childWrapperStyle,
            },
          });

          const content = (
            <React.Fragment>
              {label ? (
                <div style={styles.label} data-component-section="label">
                  {label}
                </div>
              ) : null}
              <div style={styles.child} data-component-section="children">
                {children}
              </div>
            </React.Fragment>
          );

          const wrapperProps = {
            "data-component-name": "Item",
            "data-component-prop-id": id,
            "data-component-prop-selected": selected,
            ...otherProps,
          };

          if (onTap == null) {
            return (
              <div {...wrapperProps} style={styles.item}>
                {content}
              </div>
            );
          } else {
            return (
              <NoStyleButton
                {...wrapperProps}
                innerRef={tapTargetRefHandler}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                }}
              >
                <div style={styles.item}>{content}</div>
              </NoStyleButton>
            );
          }
        }}
      </Tappable>
    );
  }
}

export default withColorsProvider(Item);

// Since the exported item is wrapped, from a public perspective, the props for
// Item are the props without colors.
export type Props = PropsWithoutColors;



// WEBPACK FOOTER //
// ../nx-components/src/ItemList/Item.jsx